/**
 * @file src/room/identity.js
 * @description Room identity — the stable per-tab client id and the user's display name.
 *
 * The client id is the PRIMARY identity of a room participant: a UUID minted once per tab and kept
 * in sessionStorage, so a reload/reconnect within the server's grace window reclaims the same seat
 * (role + grants). It is deliberately NOT the auth user id — rooms work for guests; the user id rides
 * along as optional metadata only.
 *
 * The display name is required by the relay to hold a seat or write chat (register without one and
 * the server seats you as a spectator), so `resolveDisplayName` always returns something sendable:
 * the persisted name when the user has chosen one, else a readable fallback derived from the client
 * id. Persisted in localStorage so it survives across tabs and sessions.
 *
 * Restart survival: sessionStorage dies with the browser, so a per-ROOM copy of the identity pair
 * also lives in localStorage ({ roomId → clientId, reclaimToken, savedAt }). Booting into a room
 * with an empty sessionStorage adopts that copy (see `ensureRoomIdentity`), so closing the whole
 * browser and coming back inside the server's grace window still reclaims the seat. sessionStorage
 * stays the PRIMARY (per-tab) store; a same-browser second tab is kept off the live tab's identity
 * by a per-identity Web Lock (locks die with their tab, so a crash frees the identity instantly).
 * Entries idle past the server's own identity sweep (24h) are pruned.
 */

const CLIENT_ID_KEY = 'orbitersRoomClientId';
const RECLAIM_TOKEN_KEY = 'orbitersRoomReclaimToken';
const DISPLAY_NAME_KEY = 'orbitersDisplayName';
const ROOM_IDENTITY_MAP_KEY = 'orbitersRoomIdentities';
// Matches the server's idle identity sweep: a binding older than this cannot reclaim anything.
const ROOM_IDENTITY_TTL_MS = 24 * 60 * 60 * 1000;

// In-memory fallbacks for environments without Web Storage (tests, private-mode edge cases) —
// identity still works for the life of the page.
let memoryClientId = null;
let memoryReclaimToken = null;

function randomUuid() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the manual id */
  }
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function safeStorage(kind) {
  try {
    const storage = kind === 'session' ? window.sessionStorage : window.localStorage;
    // Probe: some browsers expose the object but throw on use.
    const probe = '__orbitersStorageProbe__';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

/** The stable per-tab client id (minted on first read, kept for the tab's life). */
export function getRoomClientId() {
  const storage = typeof window !== 'undefined' ? safeStorage('session') : null;
  if (!storage) {
    if (!memoryClientId) memoryClientId = randomUuid();
    return memoryClientId;
  }
  let id = storage.getItem(CLIENT_ID_KEY);
  if (!id) {
    id = randomUuid();
    storage.setItem(CLIENT_ID_KEY, id);
  }
  return id;
}

/**
 * The server-minted reclaim token proving THIS tab owns its clientId, or null before the first
 * register ack. clientIds are visible to every room peer (grant maps, peer lists), so possession of
 * one must never grant the seat — reconnect-reclaim presents this token instead. Kept in
 * sessionStorage next to the clientId so a same-tab refresh reclaims its seat.
 */
export function getRoomReclaimToken() {
  const storage = typeof window !== 'undefined' ? safeStorage('session') : null;
  if (!storage) return memoryReclaimToken;
  return storage.getItem(RECLAIM_TOKEN_KEY) || null;
}

/**
 * Adopt the identity the relay acknowledged. The ack's clientId is the EFFECTIVE one — when a
 * reclaim fails (wrong/absent token) the server minted a FRESH clientId, and this tab must carry
 * on as that new participant (typically a spectator): keeping the old id would break echo
 * suppression and ownership checks against server-stamped events. Persists both values so the
 * next register (auto-reconnect, same-tab refresh) reclaims THIS identity. When `roomId` is given,
 * the effective pair is also mirrored into the per-room localStorage copy so a full browser
 * restart into the same room can still reclaim (see `ensureRoomIdentity`).
 * @param {{ clientId?: string|null, reclaimToken?: string|null }} identity
 * @param {string|null} [roomId] the room this identity is registering for (refreshes its restart copy).
 */
export function adoptRoomIdentity({ clientId, reclaimToken } = {}, roomId = null) {
  const storage = typeof window !== 'undefined' ? safeStorage('session') : null;
  // Always mirror into memory (not only when storage is missing): if storage flips unavailable
  // later, the getters then fall back to the LAST ADOPTED pair instead of minting a fresh id
  // that no longer matches the token — id and token must move together.
  if (typeof clientId === 'string' && clientId.length > 0) {
    memoryClientId = clientId;
    if (storage) storage.setItem(CLIENT_ID_KEY, clientId);
  }
  if (typeof reclaimToken === 'string' && reclaimToken.length > 0) {
    memoryReclaimToken = reclaimToken;
    if (storage) storage.setItem(RECLAIM_TOKEN_KEY, reclaimToken);
  }
  if (roomId) saveRoomIdentityCopy(roomId);
}

// ── per-room restart copy (localStorage) ────────────────────────────────────

/** Parse the per-room identity map, dropping anything unreadable. */
function readRoomIdentityMap(storage) {
  try {
    const parsed = JSON.parse(storage.getItem(ROOM_IDENTITY_MAP_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Drop entries older than the server's identity sweep. Mutates `map`; true when anything went. */
function pruneRoomIdentityMap(map, now) {
  let changed = false;
  for (const [key, entry] of Object.entries(map)) {
    if (!Number.isFinite(entry?.savedAt) || now - entry.savedAt > ROOM_IDENTITY_TTL_MS) {
      delete map[key];
      changed = true;
    }
  }
  return changed;
}

/** Mirror the CURRENT effective identity pair into the room's restart copy (both parts required). */
function saveRoomIdentityCopy(roomId) {
  const local = typeof window !== 'undefined' ? safeStorage('local') : null;
  if (!local) return;
  // Same precedence as the getters: storage is the truth while it works; memory only backfills
  // a storage-less environment (id and token stay a matched pair either way).
  const session = safeStorage('session');
  const clientId = session ? session.getItem(CLIENT_ID_KEY) : memoryClientId;
  const reclaimToken = session ? session.getItem(RECLAIM_TOKEN_KEY) : memoryReclaimToken;
  if (!clientId || !reclaimToken) return; // nothing reclaimable without the full pair
  const map = readRoomIdentityMap(local);
  pruneRoomIdentityMap(map, Date.now());
  map[roomId] = { clientId, reclaimToken, savedAt: Date.now() };
  try {
    local.setItem(ROOM_IDENTITY_MAP_KEY, JSON.stringify(map));
  } catch {
    /* quota/unavailable — restart survival is best-effort */
  }
}

/** The room's stored restart identity ({ clientId, reclaimToken }) or null; prunes stale entries. */
function readRoomIdentityCopy(roomId) {
  const local = typeof window !== 'undefined' ? safeStorage('local') : null;
  if (!local) return null;
  const map = readRoomIdentityMap(local);
  if (pruneRoomIdentityMap(map, Date.now())) {
    try { local.setItem(ROOM_IDENTITY_MAP_KEY, JSON.stringify(map)); } catch { /* best-effort */ }
  }
  const entry = map[roomId];
  return typeof entry?.clientId === 'string' && entry.clientId.length > 0 &&
    typeof entry?.reclaimToken === 'string' && entry.reclaimToken.length > 0
    ? { clientId: entry.clientId, reclaimToken: entry.reclaimToken }
    : null;
}

// Identities this tab holds the Web Lock for (dedup — one request per identity per tab).
const heldIdentityLocks = new Set();

/**
 * Hold the per-identity Web Lock for this tab's life. The lock is what keeps a SECOND tab of the
 * same browser from adopting a live tab's identity out of localStorage — locks are same-browser
 * (exactly the localStorage scope) and die with their tab, crash included.
 * @returns {Promise<boolean|null>} true = held (or already held), false = another tab holds it,
 *   null = no Web Locks in this environment (no arbitration possible).
 */
function holdIdentityLock(clientId) {
  try {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
    if (!locks?.request) return Promise.resolve(null);
    if (heldIdentityLocks.has(clientId)) return Promise.resolve(true);
    return new Promise((resolve) => {
      locks
        .request(`orbitersRoomIdentity:${clientId}`, { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(false);
            return undefined;
          }
          heldIdentityLocks.add(clientId);
          resolve(true);
          return new Promise(() => {}); // never settles — the lock is held until the tab dies
        })
        .catch(() => resolve(null));
    });
  } catch {
    return Promise.resolve(null);
  }
}

/**
 * Resolve the identity to register with for `roomId`, BEFORE the first register goes out.
 * sessionStorage is the primary store (per-tab: a refresh reclaims as before). With an EMPTY
 * sessionStorage (a browser restart), the room's localStorage copy is adopted instead — unless
 * another live tab of this same browser holds that identity's Web Lock, in which case this tab
 * proceeds with a fresh identity (the live member is never contested; the server would arbitrate
 * a raced claim anyway — the newest connection wins, so not racing at all is the safe behavior).
 * @param {string|null} roomId
 * @returns {Promise<string>} the clientId this tab will register with.
 */
export async function ensureRoomIdentity(roomId) {
  const storage = typeof window !== 'undefined' ? safeStorage('session') : null;
  const existing = storage ? storage.getItem(CLIENT_ID_KEY) : memoryClientId;
  if (existing) {
    void holdIdentityLock(existing);
    return existing;
  }
  const saved = roomId ? readRoomIdentityCopy(roomId) : null;
  if (saved) {
    const held = await holdIdentityLock(saved.clientId);
    if (held !== false) {
      adoptRoomIdentity(saved, roomId);
      return saved.clientId;
    }
  }
  const fresh = getRoomClientId();
  void holdIdentityLock(fresh);
  return fresh;
}

/** The user's chosen display name, or null when never set. */
export function getRoomDisplayName() {
  const storage = typeof window !== 'undefined' ? safeStorage('local') : null;
  const raw = storage?.getItem(DISPLAY_NAME_KEY) ?? null;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed.length > 0 ? trimmed : null;
}

/** Persist the user's display name (empty/blank clears it). */
export function setRoomDisplayName(name) {
  const storage = typeof window !== 'undefined' ? safeStorage('local') : null;
  if (!storage) return;
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (trimmed.length > 0) storage.setItem(DISPLAY_NAME_KEY, trimmed);
  else storage.removeItem(DISPLAY_NAME_KEY);
}

/**
 * The display name to SEND: the persisted choice, else a readable fallback from the client id.
 * The relay seats a register with no displayName as a spectator, so joining as a player always
 * needs a non-empty name — the fallback keeps the flow working before any naming UI exists.
 */
export function resolveDisplayName(clientId = getRoomClientId()) {
  return getRoomDisplayName() ?? `xPlorer-${String(clientId).replace(/[^a-zA-Z0-9]/g, '').slice(0, 4)}`;
}
